import * as React from "react";
import { toast } from "sonner";
import { Umbrella, Save } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/** Holiday leave balance panel — tracks HOLIDAY only.
 *   Sick + unpaid are noted as recorded-not-balanced.
 */
export default function LeaveBalancePanel({ companyId, userId, canManage, canView, testidPrefix = "hr-balance" }) {
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [form, setForm] = React.useState({ hours: "", standard_day_hours: "8" });
  const currentYear = React.useMemo(() => new Date().getUTCFullYear(), []);

  const load = React.useCallback(async () => {
    if (!canView) { setData(null); return; }
    try {
      const { data } = await api.get(`/companies/${companyId}/hr/leave/balance/${userId}`, {
        params: { year: currentYear },
      });
      setData(data);
      setForm({
        hours: String(data.holiday?.allowance_hours ?? ""),
        standard_day_hours: String(data.holiday?.standard_day_hours ?? "8"),
      });
    } catch (e) {
      setData(null);
    }
  }, [companyId, userId, canView, currentYear]);
  React.useEffect(() => { load(); }, [load]);

  async function saveAllowance() {
    if (busy) return;
    setBusy(true);
    try {
      await api.put(`/companies/${companyId}/hr/leave/allowance/${userId}`, {
        year: currentYear,
        hours: parseFloat(form.hours) || 0,
        standard_day_hours: parseFloat(form.standard_day_hours) || 8,
      });
      toast.success("Allowance saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  }

  if (!canView) return <div className="text-[12px] text-brand-muted">You can view your own balance only.</div>;
  if (data === null) return <div className="text-[12px] text-brand-muted">Loading balance…</div>;

  const h = data.holiday || {};
  const remaining = h.remaining_hours ?? 0;
  const pending = h.pending_hours ?? 0;
  const remainingClass = remaining < 0 ? "text-brand-coral" : remaining < (h.standard_day_hours || 8) ? "text-brand-amber" : "text-brand-ink";

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-panel`}>
      <div className="flex items-center gap-1.5">
        <Umbrella size={13} className="text-brand-muted" />
        <div className="micro-label">Holiday balance · {data.year}</div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <BalanceCell label="Allowance" value={`${h.allowance_hours}h`} testid={`${testidPrefix}-allowance`} />
        <BalanceCell label="Used" value={`${h.used_hours}h`} testid={`${testidPrefix}-used`} />
        <BalanceCell label="Pending" value={`${pending}h`} testid={`${testidPrefix}-pending`} />
        <BalanceCell label="Remaining" value={`${remaining}h`} valueClass={remainingClass} testid={`${testidPrefix}-remaining`} />
      </div>

      <div className="text-[11px] text-brand-muted">
        Standard day: <span className="mono-num text-brand-ink">{h.standard_day_hours}h</span>
        {"  ·  "}
        Not balance-tracked: <span className="mono-num text-brand-ink">sick, unpaid</span>
        {" (recorded as leave, but not counted against allowance)"}
      </div>

      {canManage && (
        <div className="pt-3 border-t border-brand-rule">
          <div className="micro-label mb-2">Set allowance ({currentYear})</div>
          <div className="grid grid-cols-[120px_140px_auto] gap-2">
            <input className="field-input !h-8 mono-num" placeholder="Hours/yr"
                   type="number" step="0.5" min="0"
                   value={form.hours}
                   onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))}
                   data-testid={`${testidPrefix}-hours-input`} />
            <input className="field-input !h-8 mono-num" placeholder="Std day (h)"
                   type="number" step="0.25" min="1" max="24"
                   value={form.standard_day_hours}
                   onChange={(e) => setForm((f) => ({ ...f, standard_day_hours: e.target.value }))}
                   data-testid={`${testidPrefix}-day-input`} />
            <button className="btn-violet !h-8" onClick={saveAllowance} disabled={busy}
                    data-testid={`${testidPrefix}-save`}>
              <Save size={13} /> Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function BalanceCell({ label, value, valueClass, testid }) {
  return (
    <div className="p-2 rounded-md bg-brand-hush border border-brand-rule" data-testid={testid}>
      <div className="micro-label">{label}</div>
      <div className={`mt-0.5 text-[15px] font-bold mono-num ${valueClass || 'text-brand-ink'}`}>{value}</div>
    </div>
  );
}
